import { MatDialog } from '@angular/material/dialog';
import { PlanLimitError } from '../../../features/plans/models/enforcement.model';
import { PlanLimitErrorService } from '../../../features/plans/services/plan-limit-error.service';
import { DocumentService } from '../../../services/document.service';
import { FrontendLoggerService } from '../../../services/frontend-logger.service';
import { DialogUploadDuplicatedComponent } from '../../shared/dialog-upload-duplicated/dialog-upload-duplicated.component';
import { DialogUploadQueuedComponent } from '../../shared/dialog-upload-queued/dialog-upload-queued.component';
import { DialogUploadSuccessComponent } from '../../shared/dialog-upload-success/dialog-upload-success.component';
import { UploadFile, UploadRequest } from './upload.models';

interface UploadResponse {
  status: string;
  message?: string;
  task_id?: string;
  document_name?: string;
}

export interface UploadLifecycleCallbacks {
  replace: (file: UploadFile) => void;
  showLimit: (error: PlanLimitError) => void;
  planLimitMessage: () => string;
  completed: () => void;
}

export class UploadLifecycleCoordinator {
  private successResults: Array<{
    document_name: string;
    folder_name: string;
    task_id?: string;
    message?: string;
  }> = [];

  constructor(
    private readonly documents: DocumentService,
    private readonly dialog: MatDialog,
    private readonly planLimitErrors: PlanLimitErrorService,
    private readonly logger: FrontendLoggerService
  ) {}

  upload(file: UploadFile, request: UploadRequest, callbacks: UploadLifecycleCallbacks): void {
    file.status = 'uploading';
    file.progress = 0;
    this.logger.info('Iniciando subida de archivo', `UploadComponent.uploadFile: ${file.file.name}`);
    this.documents.uploadDocument(
      file.file,
      request.folderId,
      request.processImmediately,
      request.folderName,
      file.tags,
      request.roleId
    ).subscribe({
      next: (response) => {
        this.handleUploadResponse(file, request.folderName, response, callbacks);
        callbacks.completed();
      },
      error: (error) => {
        this.handleError(file, error, 'upload', callbacks);
        callbacks.completed();
      }
    });
  }

  replace(file: UploadFile, roleId: string, folderName: string, callbacks: UploadLifecycleCallbacks): void {
    this.documents.replaceDocument(file.file, file.file.name, roleId).subscribe({
      next: (response) => {
        file.status = response.status === 'error' ? 'error' : 'created';
        file.progress = 100;
        file.message = response.message || (response.status === 'error'
          ? 'Error en la subida'
          : 'Archivo reemplazado correctamente');
        if (response.status === 'queued') {
          this.dialog.open(DialogUploadQueuedComponent, { data: { task_id: response.task_id } });
        } else if (response.status === 'replaced') {
          this.dialog.open(DialogUploadSuccessComponent, {
            data: { document_name: response.document_name || file.file.name, folder_name: folderName }
          });
        }
        this.logger.info('Archivo reemplazado correctamente', `UploadComponent.replaceFile: ${file.file.name}`);
        callbacks.completed();
      },
      error: (error) => {
        this.handleError(file, error, 'upload', callbacks, 'Error al reemplazar el archivo');
        callbacks.completed();
      }
    });
  }

  finishBatch(files: UploadFile[], reset: () => void): void {
    if (files.some((file) => file.status === 'uploading') || this.successResults.length === 0) {
      return;
    }
    const dialogRef = this.dialog.open(DialogUploadSuccessComponent, {
      data: { results: this.successResults }
    });
    dialogRef.afterClosed().subscribe(reset);
  }

  reset(): void {
    this.successResults = [];
  }

  private handleUploadResponse(
    file: UploadFile,
    folderName: string,
    response: UploadResponse,
    callbacks: UploadLifecycleCallbacks
  ): void {
    file.progress = 100;
    file.message = response.message || (response.status === 'error'
      ? 'Error en la subida'
      : 'Archivo procesado correctamente');
    if (response.status === 'queued') {
      this.dialog.open(DialogUploadQueuedComponent, { data: { task_id: response.task_id } });
    } else if (response.status === 'duplicate') {
      this.dialog.open(DialogUploadDuplicatedComponent, {
        data: { document_name: response.document_name || file.file.name }
      }).afterClosed().subscribe((result) => {
        if (result === 'replace') {
          callbacks.replace(file);
        }
      });
    } else if (response.status === 'created' || response.status === 'success') {
      file.status = 'created';
      this.successResults.push({
        document_name: response.document_name || file.file.name,
        folder_name: folderName,
        task_id: response.task_id,
        message: file.message
      });
    }
    this.logger.info('Archivo subido correctamente', `UploadComponent.uploadFile: ${file.file.name}`);
  }

  private handleError(
    file: UploadFile,
    error: any,
    operation: 'upload',
    callbacks: UploadLifecycleCallbacks,
    fallback = 'Error al procesar el archivo'
  ): void {
    const planLimit = this.planLimitErrors.fromHttpError(error, operation);
    if (planLimit) {
      callbacks.showLimit(planLimit);
      file.status = 'blocked';
      file.message = callbacks.planLimitMessage();
      return;
    }
    file.status = 'error';
    file.message = error.error?.message || fallback;
    this.logger.error(`Error al ${fallback.includes('reemplazar') ? 'reemplazar' : 'subir'} archivo: ${file.message}`,
      `UploadComponent.${fallback.includes('reemplazar') ? 'replaceFile' : 'uploadFile'}: ${file.file.name}`);
  }
}
